import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, mangaList } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug);
  const [filteredManga, setFilteredManga] = useState(mangaList);

  useEffect(() => {
    setActiveCategory(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    if (slug === 'all') {
      setFilteredManga(mangaList);
    } else {
      setFilteredManga(mangaList.filter(m => m.categorySlug === slug));
    }
  }, [slug]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`);
  };

  const currentCategory = categories.find(c => c.slug === slug);
  const pageTitle = slug === 'all'
    ? '全部分类-韩漫免费漫画在线-好看的韩国漫画'
    : `${currentCategory?.name || ''}漫画-韩漫免费漫画在线-韩漫之家`;

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmanzhijia.com/' },
          { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://www.hanmanzhijia.com/category/all' },
          { '@type': 'ListItem', position: 3, name: currentCategory?.name || '全部漫画' },
        ],
      },
      {
        '@type': 'ItemList',
        itemListElement: filteredManga.map((manga, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'CreativeWork',
            name: manga.title,
            author: { '@type': 'Person', name: manga.author },
            url: `https://www.hanmanzhijia.com/manga/${manga.slug}`,
          },
        })),
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb />

        <main className="pb-14">
          <section className="pt-8 pb-4">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {slug === 'all' ? '全部分类' : `${currentCategory?.name || ''}漫画`}
              </h1>
              <p className="text-sm text-foreground-600">
                浏览所有分类的韩国漫画，发现您喜欢的作品
              </p>
            </div>
          </section>

          <section className="sticky top-16 z-40 bg-background-50 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 py-3 overflow-x-auto scrollbar-hide">
                {categories.map(cat => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className="pt-8">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              {filteredManga.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">暂无该分类的漫画</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 md:gap-6">
                  {filteredManga.map(manga => (
                    <Link
                      key={manga.id}
                      to={`/manga/${manga.slug}`}
                      className="group no-underline"
                    >
                      <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4] bg-background-100">
                        <img
                          src={manga.coverImage}
                          alt={`${manga.title} - 韩漫在线漫画`}
                          title={`${manga.title} - 好看的韩国漫画`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 text-xs font-medium bg-primary-500 text-white rounded-md">
                            {manga.rating}
                          </span>
                        </div>
                        <div className="absolute top-2 right-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-md text-white ${
                            manga.status === '连载中' ? 'bg-accent-500' : 'bg-secondary-500'
                          }`}>
                            {manga.status}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {manga.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">
                        {manga.author} · {manga.category}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}